import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { competitionsApi } from '@/api/competitions';
import { submissionsApi } from '@/api/submissions';
import { judgesApi } from '@/api/judges';
import { judgingApi } from '@/api/judging';
import { categoriesApi } from '@/api/categories';
import {
  CompetitionStatus,
  SubmissionStatus,
  type Competition,
  type Submission,
} from '@/api/types';
import { useAuthStore } from '@/store/authStore';
import { hasRole } from '@/lib/roles';
import { useCompetitions, useMyCompetitions } from './useCompetitions';
import { submissionKeys } from './useSubmissions';
import { judgeAssignmentKeys } from './useJudging';
import { competitionParticipantKeys } from './useCompetitionParticipants';
import { categoryKeys } from './useCategories';

/** Everything the organizer overview shows about one competition, computed from real API data only. */
export interface CompetitionOverview {
  competition: Competition;
  categoryCount: number;
  /** Submissions that competitors have actually sent in (everything except drafts). */
  entries: number;
  accepted: number;
  /** Handed in and not rejected yet (they are accepted when judging starts). */
  awaitingModeration: Submission[];
  rejected: number;
  judgeUserIds: string[];
  /** Scores judges have given so far (sum of SubmissionRanking.scoreCount). */
  scoresReceived: number;
  /** accepted × judges — every judge scoring every accepted entry. */
  scoresExpected: number;
  isLoading: boolean;
  isError: boolean;
}

const isJudgingOrDone = (s: CompetitionStatus) => s >= CompetitionStatus.Judging && s < CompetitionStatus.Archived;

/** Keeps the dashboard to a sane number of parallel requests (4 per competition). */
const MAX_COMPETITIONS = 12;

/**
 * Aggregates the competitions the caller manages (SuperAdmin: all of them),
 * excluding archived ones, into per-competition and total figures for the
 * organizer overview. Every number here comes from an existing endpoint:
 * submissions, judge assignments, participants and the ranking (for score
 * counts). There is no per-judge progress endpoint for organizers, so
 * judging progress is overall coverage, not a per-judge breakdown.
 */
export function useOrganizerOverview() {
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = hasRole(user, 'SuperAdmin');
  const myCompetitions = useMyCompetitions();
  const competitions = useCompetitions({ page: 1, pageSize: 100 });

  const managed = useMemo(() => {
    const all = competitions.data?.items ?? [];
    const managedIds = new Set((myCompetitions.data?.items ?? []).filter((c) => c.canManage).map((c) => c.competitionId));
    return all
      .filter((c) => c.status !== CompetitionStatus.Archived)
      .filter((c) => isSuperAdmin || managedIds.has(c.id))
      .slice(0, MAX_COMPETITIONS);
  }, [competitions.data, myCompetitions.data, isSuperAdmin]);

  const submissionQueries = useQueries({
    queries: managed.map((c) => ({
      queryKey: submissionKeys.list({ competitionId: c.id }),
      queryFn: () => submissionsApi.list({ competitionId: c.id }),
    })),
  });
  const judgeQueries = useQueries({
    queries: managed.map((c) => ({
      queryKey: judgeAssignmentKeys.list(c.id),
      queryFn: () => judgesApi.listForCompetition(c.id),
    })),
  });
  const participantQueries = useQueries({
    queries: managed.map((c) => ({
      queryKey: competitionParticipantKeys.list(c.id),
      queryFn: () => competitionsApi.listParticipants(c.id),
    })),
  });
  const categoryQueries = useQueries({
    queries: managed.map((c) => ({
      queryKey: categoryKeys.forCompetition(c.id),
      queryFn: () => categoriesApi.listForCompetition(c.id),
    })),
  });
  const rankingQueries = useQueries({
    queries: managed.map((c) => ({
      queryKey: ['judging', 'ranking', c.id] as const,
      queryFn: () => judgingApi.getRanking(c.id),
      enabled: isJudgingOrDone(c.status),
    })),
  });

  const rows: CompetitionOverview[] = managed.map((competition, i) => {
    const submissions = submissionQueries[i].data?.items ?? [];
    const judgeUserIds = (judgeQueries[i].data?.items ?? []).map((j) => j.userId);
    const ranking = rankingQueries[i].data?.items ?? [];
    const accepted = submissions.filter((s) => s.status === SubmissionStatus.Accepted).length;
    return {
      competition,
      categoryCount: categoryQueries[i].data?.items.length ?? 0,
      entries: submissions.filter((s) => s.status !== SubmissionStatus.Draft).length,
      accepted,
      awaitingModeration: submissions.filter((s) => s.status === SubmissionStatus.Submitted),
      rejected: submissions.filter((s) => s.status === SubmissionStatus.Rejected).length,
      judgeUserIds,
      scoresReceived: ranking.reduce((sum, r) => sum + r.scoreCount, 0),
      scoresExpected: accepted * judgeUserIds.length,
      isLoading:
        submissionQueries[i].isLoading ||
        judgeQueries[i].isLoading ||
        participantQueries[i].isLoading ||
        (isJudgingOrDone(competition.status) && rankingQueries[i].isLoading),
      isError:
        submissionQueries[i].isError || judgeQueries[i].isError || participantQueries[i].isError || rankingQueries[i].isError,
    };
  });

  const judging = rows.filter((r) => r.competition.status === CompetitionStatus.Judging);
  const totals = {
    active: rows.filter((r) => r.competition.status === CompetitionStatus.Active || r.competition.status === CompetitionStatus.Review).length,
    judging: judging.length,
    scheduled: rows.filter((r) => r.competition.status === CompetitionStatus.Scheduled).length,
    entries: rows.reduce((sum, r) => sum + r.entries, 0),
    accepted: rows.reduce((sum, r) => sum + r.accepted, 0),
    awaitingModeration: rows.reduce((sum, r) => sum + r.awaitingModeration.length, 0),
    scoresReceived: judging.reduce((sum, r) => sum + r.scoresReceived, 0),
    scoresExpected: judging.reduce((sum, r) => sum + r.scoresExpected, 0),
  };

  return {
    rows,
    totals,
    isLoading: competitions.isLoading || myCompetitions.isLoading,
    isError: competitions.isError,
    error: competitions.error,
    refetch: competitions.refetch,
  };
}
